///
/// EarthBrowser
///
/// Created by Matt Giger on 1/10/2026
/// Copyright (c) 2026 Geodata Labs
///

import { TextureOverlay } from "@/earthbrowser/datasets/TextureOverlay"
import { precipAltitudeScale } from "@/earthbrowser/datasets/precipAltitude"
import { type TextureFrame, TextureSeries } from "@/earthbrowser/datasets/TextureSeries"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { wgs } from "@/earthbrowser/utils/geographic"
import { isMobile } from "@/earthbrowser/utils/platform"
import { type Material } from "@stovokor/shader/Material"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type matrix } from "@stovokor/math/matrix"
import { vec4 } from "@stovokor/math/vector"

// CONUS bounds in normalized texture coordinates
// Lon: -130 to -60 -> ((-130 + 180) / 360, (-60 + 180) / 360) = (0.139, 0.333)
// Lat: 20 to 55 -> ((90 - 55) / 180, (90 - 20) / 180) = (0.194, 0.389)
// Format: [minU, minV, maxU, maxV]
const cConusBounds = new vec4(0.139, 0.194, 0.333, 0.389)

/**
 * TextureSeries tuned for radar-style frames: purges frame textures outside the
 * preload window so GPU memory stays bounded while animating or scrubbing time
 * (purged frames reload quickly from the SQLite image cache), and preloads fewer
 * frames on mobile to limit concurrent decode memory spikes.
 */
export class RadarTextureSeries<FrameType extends TextureFrame = TextureFrame> extends TextureSeries<FrameType> {
	constructor(context: EarthBrowserContext) {
		super(context)
		this.purgeUnused = true
		this.preloadCount = isMobile() ? 2 : 5
	}
}

export class MrmsOverlay extends TextureOverlay {
	private context: EarthBrowserContext

	constructor(context: EarthBrowserContext) {
		super(context)
		this.context = context
		this.textureSeries = new RadarTextureSeries(context)
		// One RG texture for the frame pair: see TextureSeries.packPairs
		this.textureSeries.packPairs = true
	}

	public overlayMaterial(): Material {
		return new MrmsOverlayMaterial(this.context, this.textureSeries)
	}
}

class MrmsOverlayMaterial implements Material {
	public shaderDefines: Record<string, string> = {
		"RADAR_TEXTURES": "1"
	}
	public available: boolean = true
	public transparency: boolean = false
	public radarFade = new UniformDynamic<number>()
	public radarBlend = new UniformDynamic<number>()
	public radarPair = new UniformDynamic<Texture>()
	// The shared overlay_radar chunk advects along radarFlow; MRMS has no
	// flow estimate, so bind zero flow (blank texture) for an in-place blend
	public radarFlow = new UniformDynamic<Texture>()
	public radarBounds = new UniformDynamic<vec4>()
	// Echo opacity ramp (datasets/precipAltitude.ts). MRMS has no refinement
	// tiles to hand off to, so the mosaic scale stays 1
	public radarAltitudeScale = new UniformDynamic<number>()
	public radarMosaicScale = new UniformDynamic<number>()
	// Footprints of the drawing 3D volumes (radar_common.glsl)
	public radarVolumeMask = new UniformDynamic<matrix>()
	public radarVolumeMaskCount = new UniformDynamic<number>()

	private context: EarthBrowserContext
	private textureSeries: TextureSeries<TextureFrame>

	constructor(context: EarthBrowserContext, textureSeries: TextureSeries<TextureFrame>) {
		this.context = context
		this.textureSeries = textureSeries
	}

	public renderUpdate(state: RenderState): void {
		this.radarFade.value = this.textureSeries.fadeFactor
		this.radarBlend.value = this.textureSeries.blendFactor
		this.radarPair.value = this.textureSeries.pairTexture
		this.radarFlow.value = this.context.blankTexture
		this.radarBounds.value = cConusBounds
		this.radarAltitudeScale.value = precipAltitudeScale(state.camera.position.length - wgs.a)
		this.radarMosaicScale.value = 1
		this.radarVolumeMask.value = this.context.radarVolumeMask.mask
		this.radarVolumeMaskCount.value = this.context.radarVolumeMask.count
	}
}
